<template>
  <div class="agent-view">
    <!-- Header -->
    <Header :title="headerTitle" @toggle-sidebar="toggleSidebar" />
    
    <!-- Barre de navigation -->
    <div class="main-container">
      <div class="back-nav">
        <button class="back-btn" @click="$router.back()">
          <span class="material-icons">arrow_back</span>
          Retour
        </button>
      </div>
    
    <!-- Loading State -->
    <div class="loading-container" v-if="loading">
      <div class="spinner"></div>
      <p>Chargement des données de l'agent...</p>
    </div>

    <!-- Error Message -->
    <div class="error-message" v-if="error">
      <span class="material-icons">error_outline</span>
      <p>{{ error }}</p>
      <button @click="fetchAgentData">
        <span class="material-icons">refresh</span>
        Réessayer
      </button>
    </div>

    <div class="agent-content" v-if="!loading && !error && agent">
      <!-- Agent Header -->
      <div class="agent-header">
        <div class="agent-avatar">
          <img 
            :src="getPhotoUrl(agent.photo)" 
            alt="Photo de l'agent"
            @error="handleImageError"
          />
        </div>
        <div class="agent-title">
          <h1>{{ agent.nom }} {{ agent.prenom }}</h1>
          <div class="agent-meta">
            <span class="matricule">{{ agent.matricule }}</span>
            <span class="direction-badge">{{ agent.direction }}</span>
            <span class="status-badge" :class="agent.status">
              {{ getStatutLabel() }}
            </span>
          </div>
        </div>
        <div class="header-actions">
          <button class="view-card-btn" @click="viewCard">
            <span class="material-icons">badge</span>
            Voir la carte
          </button>
          <button class="edit-btn" @click="editAgent">
            <span class="material-icons">edit</span>
            Modifier
          </button>
          <button class="print-btn" @click="printAgentProfile">
            <span class="material-icons">print</span>
            Imprimer
          </button>
        </div>
      </div>

      <!-- Agent Details -->
      <div class="agent-details">
        <div class="details-card">
          <h3>Informations personnelles</h3>
          <div class="details-grid">
            <div class="detail-item">
              <div class="detail-label">Nom complet</div>
              <div class="detail-value">{{ agent.nom }} {{ agent.prenom }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">Sexe</div>
              <div class="detail-value">{{ agent.sexe === 'M' ? 'Masculin' : 'Féminin' }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">Date de naissance</div>
              <div class="detail-value">{{ formatDate(agent.date_naissance) }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">Adresse</div>
              <div class="detail-value">{{ agent.adresse || 'Non spécifiée' }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">Téléphone</div>
              <div class="detail-value">{{ agent.telephone || 'Non spécifié' }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">Email</div>
              <div class="detail-value">{{ agent.email || 'Non spécifié' }}</div>
            </div>
          </div>
        </div>

        <div class="details-card">
          <h3>Informations professionnelles</h3>
          <div class="details-grid">
            <div class="detail-item">
              <div class="detail-label">Matricule</div>
              <div class="detail-value">{{ agent.matricule }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">Direction</div>
              <div class="detail-value">{{ getDirectionName() }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">Fonction</div>
              <div class="detail-value">{{ getFonctionName() }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">Date d'embauche</div>
              <div class="detail-value">{{ formatDate(agent.date_recrutement || agent.date_embauche) }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">Type de contrat</div>
              <div class="detail-value">{{ agent.type_contrat || 'Non spécifié' }}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">Statut</div>
              <div class="detail-value status-text" :class="getStatutClass()">
                {{ getStatutLabel() }}
              </div>
            </div>
          </div>
        </div>

        <div class="details-card">
          <h3>Informations sociales</h3>
          <div class="details-grid">
            <div class="detail-item">
              <div class="detail-label">État civil</div>
              <div class="detail-value">{{ getEtatCivilLabel(agent.etat_civil) || 'Non spécifié' }}</div>
            </div>
            <template v-if="agent.etat_civil === 'marie'">
              <div class="detail-item">
                <div class="detail-label">Nom du conjoint</div>
                <div class="detail-value">{{ agent.nom_conjoint || 'Non spécifié' }}</div>
              </div>
              <div class="detail-item">
                <div class="detail-label">Contact du conjoint</div>
                <div class="detail-value">{{ agent.contact_conjoint || 'Non spécifié' }}</div>
              </div>
            </template>
            <div class="detail-item">
              <div class="detail-label">Nombre d'enfants</div>
              <div class="detail-value">{{ agent.nombre_enfants ?? '0' }}</div>
            </div>
            <div class="detail-item" v-if="getNomsEnfantsList(agent).length > 0">
              <div class="detail-label">Noms des enfants</div>
              <div class="detail-value">
                <ul class="noms-enfants-list">
                  <li v-for="(nom, idx) in getNomsEnfantsList(agent)" :key="idx">{{ nom }}</li>
                </ul>
              </div>
            </div>
            <div class="detail-item">
              <div class="detail-label">Contact d'urgence</div>
              <div class="detail-value">{{ agent.contact_urgence || 'Non spécifié' }}</div>
            </div>
          </div>
        </div>

        <!-- Agent Attendance Section -->
        <div class="details-card attendance-section">
          <div class="card-header">
            <h3>Pointages</h3>
            <div class="date-range-controls">
              <button 
                class="period-btn" 
                :class="{ active: selectedPeriod === 'month' }"
                @click="setPeriod('month')"
              >
                Mois courant
              </button>
              <button 
                class="period-btn" 
                :class="{ active: selectedPeriod === 'custom' }"
                @click="setPeriod('custom')"
              >
                Période personnalisée
              </button>
              
              <div class="date-range-picker" v-if="selectedPeriod === 'custom'">
                <div class="date-input">
                  <label for="start-date">Du:</label>
                  <input 
                    type="date" 
                    id="start-date" 
                    v-model="dateRange.start"
                    @change="fetchAttendanceData"
                  />
                </div>
                <div class="date-input">
                  <label for="end-date">Au:</label>
                  <input 
                    type="date" 
                    id="end-date" 
                    v-model="dateRange.end"
                    @change="fetchAttendanceData"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div class="attendance-stats">
            <div class="stat-item">
              <div class="stat-value">{{ attendanceStats.totalDays }}</div>
              <div class="stat-label">Jours</div>
            </div>
            <div class="stat-item present">
              <div class="stat-value">{{ attendanceStats.present }}</div>
              <div class="stat-label">Présences</div>
            </div>
            <div class="stat-item late">
              <div class="stat-value">{{ attendanceStats.late }}</div>
              <div class="stat-label">Retards</div>
            </div>
            <div class="stat-item absent">
              <div class="stat-value">{{ attendanceStats.absent }}</div>
              <div class="stat-label">Absences</div>
            </div>
            <div class="stat-item other">
              <div class="stat-value">{{ attendanceStats.other }}</div>
              <div class="stat-label">Autres</div>
            </div>
          </div>

          <div class="attendance-table" v-if="!loadingAttendance && agentAttendance.length > 0">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Heure d'arrivée</th>
                  <th>Heure de départ</th>
                  <th>Durée</th>
                  <th>Motif</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in agentAttendance" :key="record.id">
                  <td>{{ formatDate(record.date) }}</td>
                  <td>{{ formatTime(record.date) }}</td>
                  <td>{{ record.heure_depart ? formatTime(record.heure_depart) : 'N/A' }}</td>
                  <td>{{ record.duree ? formatDuration(record.duree) : 'N/A' }}</td>
                  <td>
                    <span :class="`motif-badge ${getMotifClass(record.motif)}`">
                      {{ record.motif }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div class="empty-state" v-else-if="!loadingAttendance && agentAttendance.length === 0">
            <span class="material-icons">event_busy</span>
            <p>Aucun pointage trouvé pour cette période</p>
          </div>
          
          <div class="loading-attendance" v-if="loadingAttendance">
            <div class="spinner-small"></div>
            <p>Chargement des pointages...</p>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- Edit Agent Modal -->
    <!-- Ce composant sera implémenté plus tard -->
    <!-- <AgentFormModal
      v-if="showEditModal"
      :agent="agent"
      :isEditing="true"
      @close="showEditModal = false"
      @save="saveAgent"
    /> -->

    <!-- Service Card Modal -->
    <div class="card-modal-overlay" v-if="showCardModal && agent" @click="showCardModal = false">
      <div class="card-modal-content" @click.stop>
        <button class="close-btn-absolute" @click="showCardModal = false">
          <span class="material-icons">close</span>
        </button>
        <div class="card-container">
          <!-- Face 2 - Verso -->
          <div class="service-card face-2">
            <div class="filigrane-container">
              <img :src="filigraneImage" alt="Filigrane" class="filigrane-image" />
            </div>
            <div class="card-logo-top">
              <div class="primature-logo-top">
                <img src="/img/primature.51dfea29.png" alt="PRIMATURE" class="primature-top">
                <div class="primature-text-top">PRIMATURE</div>
            </div>
              <img :src="logoImage" alt="Logo" class="center-logo" />
            </div>
            <div class="card-header-verso">
              <h4>CARTE DE SERVICE N°{{ getCardNumber() }}/{{ new Date().getFullYear().toString().slice(2) }}</h4>
            </div>
            <div class="card-body-verso">
              <div class="photo-section">
                <img 
                  :src="getAgentPhoto()" 
                  alt="Photo agent" 
                  class="agent-photo"
                  @error="handlePhotoError"
                  @load="handlePhotoLoad"
                />
                <div v-if="photoError" class="photo-placeholder">
                  <span class="material-icons">person</span>
                </div>
              </div>
              <div class="info-section-wrapper">
                <div class="qr-code-right">
                  <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="QR Code" class="qr-code-image" />
                  <div v-else class="qr-placeholder">QR</div>
                </div>
                <div class="info-blocks">
                  <div class="info-top-row">
                    <div class="info-block">
                      <div class="info-row">
                        <span class="info-label">Nom:</span>
                        <span class="info-value">{{ getFirstName() }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Matricule:</span>
                        <span class="info-value">{{ agent.matricule }}</span>
                      </div>
                    </div>
                    <div class="info-block">
                      <div class="info-row">
                        <span class="info-label">Prenom:</span>
                        <span class="info-value">{{ getLastName() }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Sexe:</span>
                        <span class="info-value">{{ agent.sexe === 'M' ? 'Masculin' : 'Féminin' }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="info-bottom-row">
                    <div class="info-block">
                      <div class="info-row">
                        <span class="info-label">Grade:</span>
                        <span class="info-value">{{ getGradeName() || 'Non spécifié' }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Direction:</span>
                        <span class="info-value">{{ getDirectionName() }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="right-bottom-section">
                  <div class="signature-section">
                    <div class="signature-cheff">
                      <img :src="signatureDg" alt="Signature DG" class="signature-image"/>
                    </div>
                    <p class="director-name">YODI SHEMBO Freddy</p>
                    <p class="director-title">Directeur Général</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="card-stripe">
              <div class="stripe blue"></div>
              <div class="stripe yellow"></div>
              <div class="stripe red"></div>
            </div>
          </div>

          <!-- Face 1 - Recto -->
          <div class="service-card face-1">
            <div class="card-top">
              <div class="primature-logo">
                <img :src="primatureLogo" alt="PRIMATURE" />
                <div class="primature-text">PRIMATURE</div>
              </div>
              <div class="center-logo-section">
                <img :src="logoImage" alt="Logo" class="center-logo-top" />
              </div>
            </div>
            <div class="card-body-recto">
              <div class="qr-code-bottom">
                <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="QR Code" class="qr-code-image" />
                <div v-else class="qr-placeholder">QR</div>
              </div>
            </div>
            <div class="card-banner">
              <p>Les autorités tant civiles que militaires sont priées d'approcher leurs assistances au porteur de la présente</p>
              <p class="website">www.apcsc.gouv.cd</p>
            </div>
          </div>
        </div>
        <div class="card-actions">
          <button class="capture-btn" @click="captureCardForPVC">
            <span class="material-icons">camera_alt</span>
            Capturer pour impression PVC
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns'
import { fr } from 'date-fns/locale'
import { usePersonnelStore } from '@/store/personnel'
import { usePointageStore } from '@/store/pointage'
import Header from '@/components/Header.vue'
import api from '@/services/api'
import QRCode from 'qrcode'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { 
  getNextCardNumber, 
  resetCardNumbers,
  getAgentCardNumber,
  getOrAssignCardNumber,
  saveCapturedCard,
  getCurrentCardNumber
} from '@/services/cardNumberService'
// import AgentFormModal from '@/components/modals/AgentFormModal.vue'

// Liste complète des agents pour récupérer les informations de direction, grade, fonction
const personnelList = ref([])

const route = useRoute()
const router = useRouter()
const personnelStore = usePersonnelStore()
const pointageStore = usePointageStore()

// Component state
const loading = ref(true)
const loadingAttendance = ref(true)
const error = ref(null)
const agent = ref(null)
const agentAttendance = ref([])
const showCardModal = ref(false)
const selectedPeriod = ref('month') // 'month' or 'custom'
const qrCodeUrl = ref(null)

// Header properties
const headerTitle = computed(() => {
  return agent.value ? `Profil de ${agent.value.nom}` : 'Profil Agent'
})

// Pour maintenir la cohérence avec les autres vues
const toggleSidebar = () => {
  // Cette fonction est requise par le Header mais n'a pas d'effet ici
  // car la barre latérale est contrôlée au niveau de l'App.vue
}
const dateRange = reactive({
  start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
  end: format(endOfMonth(new Date()), 'yyyy-MM-dd')
})

// Stats calculations
const attendanceStats = computed(() => {
  if (!agentAttendance.value.length) {
    return {
      totalDays: 0,
      present: 0,
      absent: 0,
      late: 0,
      other: 0
    }
  }

  const stats = agentAttendance.value.reduce((acc, record) => {
    const motifLower = record.motif.toLowerCase()
    if (motifLower.includes('présent')) {
      acc.present++
    } else if (motifLower.includes('retard')) {
      acc.late++
    } else if (motifLower.includes('absent') || motifLower.includes('congé') || motifLower.includes('permission')) {
      acc.absent++
    } else {
      acc.other++
    }
    return acc
  }, { present: 0, absent: 0, late: 0, other: 0 })

  stats.totalDays = agentAttendance.value.length
  return stats
})

// Charger la liste complète des agents depuis /api/personnel-liste
const loadPersonnelList = async () => {
  try {
    const response = await api.get('/api/personnel-liste')
    if (response && response.data) {
      if (response.data.success && response.data.data) {
        personnelList.value = response.data.data
      } else if (Array.isArray(response.data)) {
        personnelList.value = response.data
      } else if (response.data.data && Array.isArray(response.data.data)) {
        personnelList.value = response.data.data
      }
      console.log('Liste du personnel chargée:', personnelList.value.length, 'agents')
    }
  } catch (err) {
    console.error('Erreur lors du chargement de la liste du personnel:', err)
  }
}

// Methods
const fetchAgentData = async () => {
  const agentId = route.params.id
  if (!agentId) {
    error.value = "ID de l'agent non spécifié"
    loading.value = false
    return
  }

  try {
    loading.value = true
    error.value = null

    // Charger la liste complète des agents pour récupérer direction, fonction, grade
    await loadPersonnelList()
    
    // Charger l'agent individuel depuis l'endpoint pour tous les autres champs
    const response = await api.getAgentById(agentId)
    
    // Gérer différentes structures de réponse possibles
    let agentFromApi = null
    if (response && response.data) {
      if (response.data.data) {
        agentFromApi = response.data.data
      } else if (response.data.id || response.data.num_personnel || response.data.matricule) {
        agentFromApi = response.data
      }
    }
    
    if (!agentFromApi) {
      error.value = "Agent non trouvé"
      loading.value = false
      return
    }
    
    // Chercher l'agent dans la liste pour récupérer direction, fonction, grade
    let agentFromList = null
    if (personnelList.value.length > 0) {
      agentFromList = personnelList.value.find(a => 
        a.id === parseInt(agentId) || 
        a.id === agentId ||
        a.num_personnel === parseInt(agentId) ||
        a.num_personnel === agentId ||
        String(a.id) === String(agentId) ||
        String(a.num_personnel) === String(agentId) ||
        (agentFromApi.num_personnel && (a.num_personnel === agentFromApi.num_personnel || a.id === agentFromApi.num_personnel)) ||
        (agentFromApi.id && (a.id === agentFromApi.id || a.num_personnel === agentFromApi.id))
      )
    }
    
    // Combiner les données : utiliser l'agent de l'API comme base
    agent.value = { ...agentFromApi }
    
    // Si on a trouvé l'agent dans la liste, extraire direction, fonction, grade depuis directionInfo, fonctionInfo, gradeInfo
    if (agentFromList) {
      // Extraire la direction depuis directionInfo
      if (agentFromList.directionInfo) {
        agent.value.directionFromList = agentFromList.directionInfo.nom || agentFromList.directionInfo.name || agentFromList.directionInfo.libelle
      } else if (agentFromList.DirectionInfo) {
        agent.value.directionFromList = agentFromList.DirectionInfo.nom || agentFromList.DirectionInfo.name || agentFromList.DirectionInfo.libelle
      } else if (agentFromList.direction_info) {
        agent.value.directionFromList = agentFromList.direction_info.nom || agentFromList.direction_info.name || agentFromList.direction_info.libelle
      }
      
      // Extraire le grade depuis gradeInfo
      if (agentFromList.gradeInfo) {
        agent.value.gradeFromList = agentFromList.gradeInfo.nom || agentFromList.gradeInfo.name || agentFromList.gradeInfo.libelle || agentFromList.gradeInfo.description
      } else if (agentFromList.GradeInfo) {
        agent.value.gradeFromList = agentFromList.GradeInfo.nom || agentFromList.GradeInfo.name || agentFromList.GradeInfo.libelle || agentFromList.GradeInfo.description
      } else if (agentFromList.grade_info) {
        agent.value.gradeFromList = agentFromList.grade_info.nom || agentFromList.grade_info.name || agentFromList.grade_info.libelle || agentFromList.grade_info.description
      }
      
      // Extraire la fonction depuis fonctionInfo
      if (agentFromList.fonctionInfo) {
        agent.value.fonctionFromList = agentFromList.fonctionInfo.nom || agentFromList.fonctionInfo.name || agentFromList.fonctionInfo.libelle || agentFromList.fonctionInfo.description
      } else if (agentFromList.FonctionInfo) {
        agent.value.fonctionFromList = agentFromList.FonctionInfo.nom || agentFromList.FonctionInfo.name || agentFromList.FonctionInfo.libelle || agentFromList.FonctionInfo.description
      } else if (agentFromList.fonction_info) {
        agent.value.fonctionFromList = agentFromList.fonction_info.nom || agentFromList.fonction_info.name || agentFromList.fonction_info.libelle || agentFromList.fonction_info.description
      }
    }
    
    if (agent.value) {
      await fetchAttendanceData()
    }
  } catch (err) {
    console.error("Erreur lors du chargement de l'agent:", err)
    error.value = "Impossible de charger les données de l'agent"
  } finally {
    loading.value = false
  }
}

const fetchAttendanceData = async () => {
  if (!agent.value?.id) return

  try {
    loadingAttendance.value = true
    
    // Si une date est spécifiée dans l'URL, la prendre comme date de début
    if (route.query.date) {
      const dateParam = route.query.date
      dateRange.start = dateParam
      dateRange.end = dateParam
      selectedPeriod.value = 'custom'
    }

    // Appel à l'action du store pour chercher les pointages de l'agent
    await pointageStore.fetchAgentPointages({
      agentId: agent.value.num_personnel,
      startDate: dateRange.start,
      endDate: dateRange.end
    })
    
    agentAttendance.value = pointageStore.agentPointages
  } catch (err) {
    console.error("Erreur lors du chargement des pointages:", err)
  } finally {
    loadingAttendance.value = false
  }
}

const setPeriod = (period) => {
  selectedPeriod.value = period
  
  if (period === 'month') {
    // Définir la plage pour le mois courant
    dateRange.start = format(startOfMonth(new Date()), 'yyyy-MM-dd')
    dateRange.end = format(endOfMonth(new Date()), 'yyyy-MM-dd')
  }
  
  fetchAttendanceData()
}

const formatDate = (dateStr) => {
  if (!dateStr) return 'Non spécifiée'
  return format(parseISO(dateStr), 'dd MMMM yyyy', { locale: fr })
}

const formatTime = (dateTimeStr) => {
  if (!dateTimeStr) return 'N/A'
  return format(parseISO(dateTimeStr), 'HH:mm', { locale: fr })
}

const formatDuration = (minutes) => {
  if (!minutes) return 'N/A'
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours}h${mins.toString().padStart(2, '0')}`
}

const getMotifClass = (motif) => {
  const motifLower = motif.toLowerCase()
  if (motifLower.includes('présent')) return 'present'
  if (motifLower.includes('retard')) return 'late'
  if (motifLower.includes('absent') || motifLower.includes('congé') || motifLower.includes('permission')) return 'absent'
  return 'other'
}

const apcscLogo = require('@/assets/images/apcsc.png')
const logoImage = require('@/assets/images/logo.png')
const primatureLogo = require('@/assets/images/primature.png')
const rdcMapImage = require('@/assets/images/Capture_rdc_or.png')
const signatureDg = require('@/assets/images/signature.png')
const filigraneImage = require('@/assets/images/filigramme.jpg')


// Variable pour stocker le numéro de carte de l'agent actuel
const currentCardNumber = ref(null)

const viewCard = () => {
  if (!agent.value) {
    console.error('Aucun agent sélectionné')
    return
  }

  // Vérifier si l'agent a déjà un numéro de carte assigné
  const existingCardNumber = getAgentCardNumber(agent.value.id)
  
  if (existingCardNumber) {
    // L'agent a déjà un numéro, l'utiliser
    currentCardNumber.value = existingCardNumber
    console.log(`Agent ${agent.value.matricule} a déjà le numéro de carte: ${existingCardNumber}`)
  } else {
    // L'agent n'a pas encore de numéro, lui en assigner un (sans incrémenter encore)
    // Le numéro sera confirmé lors de l'impression
    const assignedNumber = getOrAssignCardNumber(agent.value, true)
    currentCardNumber.value = assignedNumber
    console.log(`Numéro de carte ${assignedNumber} assigné à l'agent ${agent.value.matricule}`)
  }
  
  showCardModal.value = true
}

const getCardNumber = () => {
  // Si on a déjà un numéro assigné pour cet agent dans cette session, l'utiliser
  if (currentCardNumber.value !== null) {
    return currentCardNumber.value.toString().padStart(3, '0')
  }
  
  // Vérifier si l'agent a déjà un numéro dans la base de données
  if (agent.value?.id) {
    const existingNumber = getAgentCardNumber(agent.value.id)
    if (existingNumber) {
      currentCardNumber.value = existingNumber
      return existingNumber.toString().padStart(3, '0')
    }
    
    // Si l'agent n'a pas encore de numéro, lui en assigner un (sans marquer comme imprimé)
    // Le numéro sera confirmé et marqué comme imprimé lors de l'impression
    if (agent.value) {
      const assignedNumber = getOrAssignCardNumber(agent.value, true)
      if (assignedNumber) {
        currentCardNumber.value = assignedNumber
        return assignedNumber.toString().padStart(3, '0')
      }
    }
  }
  
  // Fallback : retourner un numéro par défaut (ne devrait pas arriver)
  return getNextCardNumber().toString().padStart(3, '0')
}

const getFirstName = () => {
  // Extraire le premier mot du nom complet
  if (!agent.value?.nom) return ''
  const nameParts = agent.value.nom.trim().split(/\s+/)
  return nameParts[0] || ''
}

const getLastName = () => {
  // Extraire le dernier mot du nom complet
  if (!agent.value?.nom) return agent.value?.prenom || ''
  const nameParts = agent.value.nom.trim().split(/\s+/)
  // Si le nom a plusieurs mots, prendre le dernier, sinon utiliser le prenom
  if (nameParts.length > 1) {
    return nameParts[nameParts.length - 1]
  }
  // Si un seul mot, utiliser le champ prenom s'il existe
  return agent.value?.prenom || ''
}

const photoError = ref(false)

const getAgentPhoto = () => {
  if (!agent.value?.photo) {
    console.log('AgentProfileView: Aucune photo trouvée pour l\'agent')
    return require('@/assets/images/user-default.png')
  }
  
  const photoPath = agent.value.photo
  const api_url = api.getApiUrl()
  
  console.log('AgentProfileView: Photo trouvée:', photoPath, 'Type:', typeof photoPath)
  
  // Si c'est une chaîne base64 (nouvelle photo sélectionnée ou capturée)
  if (typeof photoPath === 'string' && photoPath.startsWith('data:image')) {
    console.log('AgentProfileView: Photo en base64 détectée')
    return photoPath
  }
  
  // Si c'est un objet File (nouvelle photo sélectionnée)
  if (photoPath instanceof File) {
    console.log('AgentProfileView: Photo File détectée')
    return URL.createObjectURL(photoPath)
  }
  
  // Si c'est déjà une URL complète (http ou https)
  if (typeof photoPath === 'string' && photoPath.startsWith('http')) {
    console.log('AgentProfileView: Photo URL complète:', photoPath)
    return photoPath
  }
  
  // Si c'est juste un nom de fichier, construire l'URL complète avec le chemin des uploads
  if (typeof photoPath === 'string' && !photoPath.startsWith('/')) {
    const fullUrl = `${api_url}${photoPath}`
    console.log('AgentProfileView: Photo construite (nom fichier):', fullUrl)
    return fullUrl
  }
  
  // Si c'est un chemin relatif commençant par /, construire l'URL complète
  if (typeof photoPath === 'string' && photoPath.startsWith('/')) {
    // Si le chemin commence déjà par /data/uploads/, utiliser directement
    if (photoPath.startsWith('/data/uploads/')) {
      const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3000'
      const fullUrl = `${apiUrl}${photoPath}`
      console.log('AgentProfileView: Photo construite (chemin /data/uploads/):', fullUrl)
      return fullUrl
    }
    // Sinon, utiliser le chemin des uploads
    const fullUrl = `${api_url}${photoPath.replace(/^\//, '')}`
    console.log('AgentProfileView: Photo construite (chemin relatif):', fullUrl)
    return fullUrl
  }
  
  // Cas par défaut
  console.log('AgentProfileView: Utilisation de l\'image par défaut')
  return require('@/assets/images/user-default.png')
}

const handlePhotoError = (event) => {
  photoError.value = true
  // Remplacer par l'image par défaut
  event.target.src = require('@/assets/images/user-default.png')
}

const handlePhotoLoad = () => {
  photoError.value = false
}

// Fonctions pour résoudre les noms - Utiliser directionFromList, gradeFromList, fonctionFromList depuis la liste
// Pour les autres champs, utiliser les données de l'endpoint individuel
const getDirectionName = () => {
  if (!agent.value) return 'Non spécifiée'
  
  // Priorité 1: Utiliser la direction extraite de la liste (directionFromList)
  if (agent.value.directionFromList) {
    return agent.value.directionFromList
  }
  
  // Fallback: utiliser directement le champ direction de l'API si disponible
  if (agent.value.direction) {
    return agent.value.direction
  }
  
  return 'Non spécifiée'
}

const getGradeName = () => {
  if (!agent.value) return 'Non spécifié'
  
  // Priorité 1: Utiliser le grade extrait de la liste (gradeFromList)
  if (agent.value.gradeFromList) {
    return agent.value.gradeFromList
  }
  
  // Fallback: utiliser directement le champ grade de l'API si disponible
  if (agent.value.grade) {
    return agent.value.grade
  }
  
  return 'Non spécifié'
}

const getFonctionName = () => {
  if (!agent.value) return 'Non spécifiée'
  
  // Priorité 1: Utiliser la fonction extraite de la liste (fonctionFromList)
  if (agent.value.fonctionFromList) {
    return agent.value.fonctionFromList
  }
  
  // Fallback: utiliser directement le champ fonction de l'API si disponible
  if (agent.value.fonction) {
    return agent.value.fonction
  }
  
  return 'Non spécifiée'
}

const getStatutLabel = () => {
  const statut = agent.value?.statut || agent.value?.status
  if (!statut) return 'Non spécifié'
  
  const statutMap = {
    'actif': 'Actif',
    'active': 'Actif',
    'inactif': 'Inactif',
    'inactive': 'Inactif',
    'conge': 'Congé',
    'formation': 'Formation',
    'detachement': 'Détachement'
  }
  
  return statutMap[statut.toLowerCase()] || statut
}

const getStatutClass = () => {
  const statut = agent.value?.statut || agent.value?.status
  if (!statut) return ''
  
  const statutLower = statut.toLowerCase()
  if (statutLower === 'actif' || statutLower === 'active') return 'active'
  if (statutLower === 'inactif' || statutLower === 'inactive') return 'inactive'
  return statutLower
}

const getEtatCivilLabel = (etatCivil) => {
  if (!etatCivil) return ''
  const map = {
    celibataire: 'Célibataire',
    marie: 'Marié(e)',
    divorce: 'Divorcé(e)',
    veuf: 'Veuf(ve)',
    pacse: 'Pacsé(e)',
    concubinage: 'Concubinage'
  }
  return map[String(etatCivil).toLowerCase()] || etatCivil
}

const getNomsEnfantsList = (a) => {
  if (!a) return []
  const n = a.noms_enfants
  if (Array.isArray(n)) return n.filter(Boolean)
  if (typeof n === 'string') {
    try {
      const parsed = JSON.parse(n)
      return Array.isArray(parsed) ? parsed.filter(Boolean) : (n.trim() ? [n.trim()] : [])
    } catch (_) {
      return n.split(',').map(s => s.trim()).filter(Boolean)
    }
  }
  return []
}

const printCard = () => {
  window.print()
}

const printAgentProfile = () => {
  window.print()
}

// Fonction pour gérer les erreurs de chargement d'image dans l'avatar
const handleImageError = (event) => {
  event.target.src = require('@/assets/images/user-default.png')
}

// Fonction pour construire l'URL de la photo pour l'avatar
const getPhotoUrl = (photoPath) => {
  const api_url = api.getApiUrl()
  
  if (!photoPath) {
    return require('@/assets/images/user-default.png')
  }
  
  // Si c'est une chaîne base64 (nouvelle photo sélectionnée ou capturée)
  if (typeof photoPath === 'string' && photoPath.startsWith('data:image')) {
    return photoPath
  }
  
  // Si c'est un objet File (nouvelle photo sélectionnée)
  if (photoPath instanceof File) {
    return URL.createObjectURL(photoPath)
  }
  
  // Si c'est déjà une URL complète (http ou https)
  if (typeof photoPath === 'string' && photoPath.startsWith('http')) {
    return photoPath
  }
  
  // Si c'est juste un nom de fichier, construire l'URL complète
  if (typeof photoPath === 'string' && !photoPath.startsWith('/')) {
    return `${api_url}${photoPath}`
  }
  
  // Si c'est un chemin relatif commençant par /, ajouter le préfixe de l'API
  if (typeof photoPath === 'string') {
    return `${api_url}${photoPath}`
  }
  
  // Cas par défaut
  return require('@/assets/images/user-default.png')
}

const editAgent = () => {
  if (!agent.value) {
    console.error('Aucun agent sélectionné')
    return
  }
  // Utiliser num_personnel si disponible, sinon utiliser l'ID de la route
  const agentId = agent.value.num_personnel || agent.value.id || route.params.id
  router.push(`/agent/edit/${agentId}`)
}

const generateQRCode = async () => {
  if (!agent.value) return
  
  try {
    // Obtenir l'ID de l'agent (priorité: id > num_personnel > matricule)
    const agentId = agent.value.id || agent.value.num_personnel || agent.value.matricule
    
    if (!agentId) {
      console.error('Impossible de générer le QR code: ID de l\'agent manquant')
      return
    }
    
    // Construire l'URL complète vers la page d'informations du personnel
    const baseUrl = window.location.origin
    const qrData = `${baseUrl}/personnel/qr/${agentId}`
    
    // Générer le QR code en tant qu'URL de données
    qrCodeUrl.value = await QRCode.toDataURL(qrData, {
      width: 200,
      margin: 1,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    })
    
    console.log('QR Code généré avec URL:', qrData)
  } catch (error) {
    console.error('Erreur lors de la génération du QR code:', error)
    qrCodeUrl.value = null
  }
}

// Watcher pour générer le QR code quand l'agent change
watch(agent, () => {
  if (agent.value) {
    generateQRCode()
    // Réinitialiser le numéro de carte pour le nouvel agent
    currentCardNumber.value = null
  }
}, { immediate: true })

const captureCardForPVC = async () => {
  try {
    if (!agent.value) {
      alert('Aucun agent sélectionné')
      return
    }

    // S'assurer que l'agent a un numéro de carte assigné
    if (currentCardNumber.value === null) {
      // Si l'agent n'a pas encore de numéro, lui en assigner un
      const assignedNumber = getOrAssignCardNumber(agent.value, true)
      currentCardNumber.value = assignedNumber
    }
    
    // Attendre que le DOM soit prêt
    await nextTick()
    
    // Dimensions d'une carte PVC standard (format carte bancaire)
    const cardWidthMM = 85.6  // Largeur en mm
    const cardHeightMM = 53.98 // Hauteur en mm
    
    // Créer un nouveau PDF avec les dimensions de la carte
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: [cardWidthMM, cardHeightMM]
    })
    
    // Capturer la face recto (face-1)
    const rectoElement = document.querySelector('.face-1')
    if (rectoElement) {
      const rectoCanvas = await html2canvas(rectoElement, {
        scale: 3, // Haute résolution pour l'impression
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false
      })
      
      const rectoImgData = rectoCanvas.toDataURL('image/png')
      // Ajouter la face recto au PDF
      pdf.addImage(rectoImgData, 'PNG', 0, 0, cardWidthMM, cardHeightMM)
    }
    
    // Ajouter une nouvelle page pour la face verso
    pdf.addPage([cardWidthMM, cardHeightMM], 'landscape')
    
    // Capturer la face verso (face-2)
    const versoElement = document.querySelector('.face-2')
    if (versoElement) {
      const versoCanvas = await html2canvas(versoElement, {
        scale: 3, // Haute résolution pour l'impression
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false
      })
      
      const versoImgData = versoCanvas.toDataURL('image/png')
      // Ajouter la face verso au PDF
      pdf.addImage(versoImgData, 'PNG', 0, 0, cardWidthMM, cardHeightMM)
    }
    
    // Générer le nom du fichier avec le matricule de l'agent
    const fileName = agent.value 
      ? `carte_pvc_${agent.value.matricule || 'agent'}_${format(new Date(), 'yyyyMMdd')}.pdf`
      : `carte_pvc_${format(new Date(), 'yyyyMMdd')}.pdf`
    
    // Télécharger le PDF
    pdf.save(fileName)
    
    // IMPORTANT: Enregistrer la carte capturée dans le fichier JSON
    if (!agent.value) {
      console.error('❌ Aucun agent sélectionné')
      return
    }
    
    // Utiliser l'ID si disponible, sinon utiliser le matricule comme identifiant
    const agentIdentifier = agent.value.id || agent.value.matricule || agent.value.num_personnel
    
    if (!agentIdentifier) {
      console.error('❌ Agent sans ID ni matricule:', agent.value)
      alert('Erreur: L\'agent n\'a pas d\'ID ni de matricule. Veuillez réessayer.')
      return
    }
    
    // S'assurer que l'agent a un numéro de carte
    if (currentCardNumber.value === null || currentCardNumber.value === undefined) {
      // Obtenir le prochain numéro disponible et l'incrémenter
      currentCardNumber.value = getNextCardNumber(true)
      console.log(`📝 Numéro ${currentCardNumber.value} assigné à l'agent ${agent.value.matricule || agentIdentifier}`)
    }
    
    // Préparer toutes les informations de l'agent pour l'enregistrement
    const agentData = {
      id: agent.value.id || agent.value.matricule || agent.value.num_personnel,
      matricule: agent.value.matricule || '',
      nom: agent.value.nom || '',
      prenom: agent.value.prenom || '',
      sexe: agent.value.sexe || '',
      date_naissance: agent.value.date_naissance || '',
      email: agent.value.email || '',
      telephone: agent.value.telephone || '',
      direction: getDirectionName(),
      direction_id: agent.value.direction_id || null,
      fonction: getFonctionName(),
      fonction_id: agent.value.fonction_id || null,
      grade: getGradeName(),
      grade_id: agent.value.grade_id || null,
      adresse: agent.value.adresse || ''
    }
    
    // Enregistrer la carte capturée
    try {
      console.log(`🔄 Enregistrement de la carte capturée...`)
      console.log(`🔄 Identifiant agent: ${agentIdentifier}`)
      console.log(`🔄 Numéro de carte: ${currentCardNumber.value}`)
      console.log(`🔄 Données agent:`, agentData)
      
        const saved = saveCapturedCard(agentIdentifier, agentData, currentCardNumber.value)
        if (saved) {
          console.log(`✅ Carte de l'agent ${agent.value.matricule} (N°${currentCardNumber.value}) enregistrée`)
          console.log(`📝 Les données sont sauvegardées dans localStorage et synchronisées avec public/data/cartes_service.json`)
        } else {
        console.error(`❌ ERREUR: La carte n'a pas pu être enregistrée`)
        alert('Erreur lors de l\'enregistrement de la carte. Veuillez réessayer.')
      }
    } catch (error) {
      console.error(`❌ Erreur lors de l'enregistrement de la carte:`, error)
      console.error(`❌ Détails de l'erreur:`, error.stack)
      alert('Erreur lors de l\'enregistrement de la carte. Veuillez réessayer.')
    }
    
    console.log(`PDF généré avec succès pour l'impression PVC - Numéro de carte: ${currentCardNumber.value}`)
  } catch (error) {
    console.error('Erreur lors de la capture de la carte:', error)
    alert('Erreur lors de la génération du PDF. Veuillez réessayer.')
  }
}

onMounted(async () => {
  // Charger directement les données de l'agent
  // Les informations de direction, grade et fonction seront récupérées depuis les données de l'agent
  await fetchAgentData()
})
</script>

<style scoped>
/* Import des variables de couleurs OpSolver */
@import '@/assets/css/variables.css';

.agent-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.main-container {
  flex: 1;
  padding: 20px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 64px);
}

.agent-content {
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

.back-nav {
  padding: 15px 20px;
  background-color: white;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.back-btn {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  color: #1976d2;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 0;
}

.back-btn .material-icons {
  margin-right: 5px;
}

.primature-text-top{
  position: absolute;
  top: 3.6rem;
  font-size: 6px;
  font-weight: bold;
  left: 4.6rem;
}

.agent-header {
  display: flex;
  align-items: center;
  margin-bottom: 25px;
  padding: 25px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.agent-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  overflow: hidden;
  border: 3px solid #E0F7F4;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.agent-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.agent-avatar .material-icons {
  font-size: 36px;
  color: #757575;
}

.agent-title {
  flex-grow: 1;
}

.agent-title h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 500;
  color: #333;
}

.agent-meta {
  display: flex;
  align-items: center;
  margin-top: 5px;
}

.matricule {
  background-color: #e0e0e0;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  margin-right: 10px;
}

.direction-badge {
  background-color: var(--turquoise-primary);
  color: white;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  margin-right: 10px;
}

.status-badge {
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
}

.status-badge.active {
  background-color: var(--turquoise-dark);
  color: white;
}

.status-badge.inactive {
  background-color: #f44336;
  color: white;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.view-card-btn, .edit-btn, .print-btn {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.view-card-btn {
  background-color: #6366f1;
  color: white;
}

.view-card-btn:hover {
  background-color: #4f46e5;
}

.edit-btn {
  background-color: var(--turquoise-primary);
  color: white;
}

.print-btn {
  background-color: white;
  color: var(--turquoise-primary);
  border: 1px solid var(--turquoise-primary);
}

.view-card-btn .material-icons, .edit-btn .material-icons, .print-btn .material-icons {
  margin-right: 5px;
  font-size: 16px;
}

.agent-details {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}

.details-card {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.details-card h3 {
  margin-top: 0;
  margin-bottom: 16px;
  font-weight: 500;
  color: #333;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 10px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 15px;
}

.detail-item {
  margin-bottom: 5px;
}

.detail-label {
  font-size: 12px;
  color: #757575;
  margin-bottom: 4px;
}

.detail-value {
  font-size: 14px;
  color: #333;
}

.noms-enfants-list {
  margin: 0;
  padding-left: 18px;
  list-style: disc;
}

.noms-enfants-list li {
  margin-bottom: 4px;
}

.status-text.active {
  color: #4caf50;
  font-weight: 500;
}

.status-text.inactive {
  color: #f44336;
  font-weight: 500;
}

/* Section pointages */
.attendance-section {
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.card-header h3 {
  margin: 0;
  border-bottom: none;
  padding-bottom: 0;
}

.date-range-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.period-btn {
  background-color: white;
  border: 1px solid #ddd;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
}

.period-btn.active {
  background-color: #2196f3;
  color: white;
  border-color: #2196f3;
}

.date-range-picker {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-left: 10px;
}

.date-input {
  display: flex;
  align-items: center;
}

.date-input label {
  margin-right: 5px;
  font-size: 13px;
}

.date-input input {
  padding: 6px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
}

.attendance-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 10px;
  margin-bottom: 20px;
}

.stat-item {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
}

.stat-item.present {
  background-color: rgba(76, 175, 80, 0.1);
  border-left: 3px solid #4caf50;
}

.stat-item.late {
  background-color: rgba(255, 152, 0, 0.1);
  border-left: 3px solid #ff9800;
}

.stat-item.absent {
  background-color: rgba(244, 67, 54, 0.1);
  border-left: 3px solid #f44336;
}

.stat-item.other {
  background-color: rgba(158, 158, 158, 0.1);
  border-left: 3px solid #9e9e9e;
}

.stat-value {
  font-size: 24px;
  font-weight: 500;
  color: #333;
}

.stat-label {
  font-size: 12px;
  color: #757575;
  margin-top: 5px;
}

.attendance-table {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

th {
  background-color: #f5f5f5;
  color: #333;
  font-weight: 500;
}

tbody tr:hover {
  background-color: #f5f5f5;
}

.motif-badge {
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 12px;
  display: inline-block;
}

.motif-badge.present {
  background-color: rgba(76, 175, 80, 0.1);
  color: #4caf50;
}

.motif-badge.late {
  background-color: rgba(255, 152, 0, 0.1);
  color: #ff9800;
}

.motif-badge.absent {
  background-color: rgba(244, 67, 54, 0.1);
  color: #f44336;
}

.motif-badge.other {
  background-color: rgba(158, 158, 158, 0.1);
  color: #9e9e9e;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: #9e9e9e;
}

.empty-state .material-icons {
  font-size: 48px;
  margin-bottom: 16px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
}

.loading-attendance {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 0;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid rgba(25, 118, 210, 0.1);
  border-radius: 50%;
  border-top-color: #1976d2;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.spinner-small {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(25, 118, 210, 0.1);
  border-radius: 50%;
  border-top-color: #1976d2;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

.error-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background-color: #ffebee;
  border-radius: 8px;
  color: #d32f2f;
  text-align: center;
}

.error-message .material-icons {
  font-size: 48px;
  margin-bottom: 16px;
}

.error-message button {
  margin-top: 16px;
  padding: 8px 16px;
  background-color: #d32f2f;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.error-message button .material-icons {
  font-size: 16px;
  margin-right: 5px;
  margin-bottom: 0;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Service Card Modal */
.card-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.card-modal-content {
  background: transparent;
  border-radius: 12px;
  max-width: 400px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.close-btn-absolute {
  position: absolute;
  top: -40px;
  right: 0;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #333;
  z-index: 1001;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: background-color 0.2s, transform 0.2s;
}

.close-btn-absolute:hover {
  background: white;
  transform: scale(1.1);
}

.close-btn-absolute .material-icons {
  font-size: 20px;
}

.card-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0;
  justify-content: center;
  align-items: center;
}

.card-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
  width: 100%;
}

.capture-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.capture-btn:hover {
  background-color: #1565c0;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.capture-btn:active {
  transform: translateY(0);
}

.capture-btn .material-icons {
  font-size: 20px;
}

.service-card {
  /* Format carte bancaire standard: 86mm x 54mm */
  /* À 96 DPI: 86mm = 3.39 inch = 325px, 54mm = 2.13 inch = 204px */
  /* Pour un meilleur rendu à l'écran, on utilise un ratio légèrement plus grand */
  width: 344px; /* ~91mm à 96 DPI pour meilleure lisibilité */
  height: 216px; /* ~57mm à 96 DPI */
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}

/* Face 1 - Recto */
.face-1 {
  display: flex;
  flex-direction: column;
}

.card-top {
  padding: 10px 12px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: linear-gradient(to bottom, white 0%, #f8f9fa 100%);
  gap: 8px;
}

.primature-logo {
  width: 70px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3px;
  padding-top: 1rem;
  border: none;
  flex-shrink: 0;
  overflow: hidden;
  gap: 2px;
}

.primature-logo img {
  width: 70px;
  height: 70px;
  object-fit: contain;
  border-radius: 4px;
}

.primature-text {
  font-size: 7px;
  font-weight: bold;
  color: #333;
  text-align: center;
  letter-spacing: 0.5px;
  line-height: 1;
  position: absolute;
  top: 6.2rem;
}

.center-logo-section {
  
  flex: 1;
}

.center-logo-top {
  width: 188px;
  height: 68px;
  object-fit: contain;
  position: absolute;
  left: 5rem;
  top: 2.5rem;
}

.qr-code-top {
  width: 75px;
  height: 75px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 3px;
  flex-shrink: 0;
  padding: 5px;
}

.card-body-recto {
  flex: 1;
  padding: 8px;
  position: relative;
}

.rdc-map-container {
  position: absolute;
  top: -110px;
  right: 12px;
  z-index: 1;
}

.rdc-map-image {
  width: 70px;
  height: 70px;
  object-fit: contain;
}

.qr-code-bottom {
  position: absolute;
  top: -28px;
  right: 12px;
  width: 75px;
  height: 75px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  padding: 5px;
}

.qr-code-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.qr-placeholder {
  font-size: 8px;
  color: #999;
  font-weight: bold;
}

.card-banner {
  background: #202131;
  color: white;
  padding: 7px 12px;
  font-size: 6.5px;
  line-height: 1.35;
}

.card-banner p {
  margin: 0;
  font-size: 9.5px;
  line-height: 17px;
  letter-spacing: .7px;
}

.card-banner p:not(.website) {
  padding-top: 1rem;
  text-align: justify;
}

.card-banner .website {
  font-size: 5.5px;
  margin-top: 3px;
  opacity: 0.95;
  text-align: center;
}

.card-stripe {
  height: 3px;
  display: flex;
}

.stripe {
  flex: 1;
}

.stripe.yellow {
  background: #ffd700;
}

.stripe.red {
  background: #dc143c;
}

.stripe.blue {
  background: #325ebd;
}

/* Face 2 - Verso */
.face-2 {
  display: flex;
  flex-direction: column;
  position: relative;
}

.filigrane-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 190%;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

.filigrane-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.05;
}

.card-logo-top {
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: white;
  position: relative;
  z-index: 1;
}

.center-logo {
  width: auto;
  height: 62px;
  padding-left: 6.5rem;
  z-index: inherit;
  object-fit: contain;
}

.card-header-verso {
  background:#512fb9;
  color: white;
  padding: 0;
  text-align: center;
  position: relative;
  z-index: 1;
}

.card-header-verso h4 {
  margin: 0;
  font-size: 15px;
  font-weight: bold;
  letter-spacing: 0.3px;
  line-height: 20px;
}

.card-body-verso {
  flex: 1;
  padding: 8px 10px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  position: relative;
  z-index: 1;
}

.photo-section {
  flex-shrink: 0;
  position: relative;
  width: 97px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.agent-photo {
  width: 100%;
  height: 107px;
  object-fit: cover !important; /* cover pour le PDF (évite l'étirement fill) */
  object-position: center;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  display: block;
  background: #f5f5f5;
}

.photo-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 3px;
  border: 1px solid #e0e0e0;
}

.photo-placeholder .material-icons {
  font-size: 40px;
  color: #999;
}

.info-section-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  min-height: 0;
  padding-top: 0;
}

.info-blocks {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
}

.info-top-row {
  display: flex;
  gap: 2rem;
  align-items: flex-start;
  flex-wrap: nowrap;
}

.info-bottom-row {
  display: flex;
  align-items: flex-start;
  width: 160px;
}

.info-bottom-row .info-block {
  width: auto;
}

.info-bottom-row .info-value {
  width: 150px;
}

.info-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  width: auto;
  min-width: 0;
}

.info-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.right-bottom-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-top: auto;
  width: 100%;
}

.info-row {
  display: flex;
  flex-direction: column;
  font-size: 7.5px;
  line-height: 1;
  margin-bottom: 0;
}

.info-label {
  font-weight: 600;
  color: #333;
  font-size: 5.4px;
  margin-bottom: 1px;
}

.info-value {
  color: #444;
  font-weight: bold;
  font-size: 9.5px;
}

.primature-top{
    width: 77px;
    position: absolute;
    top: rem;
    top: -.8rem;
    right: 16rem;
  }
  

.qr-code-right {
  width: 65px;
  height: 65px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border-radius: 3px;
  flex-shrink: 0;
  position: absolute;
  top: 46px;
  right: 0rem;
  padding: 0;
}

.delivery-date {
  font-size: 6px;
  color: rgb(0, 0, 0)k;
  text-align: center;
  margin-top: 2px;
  font-weight: bold;
  position: absolute;
  top: 6.8rem;
}

.signature-section {
  text-align: center;
  width: 100%;
  position: absolute;
  top: 7.3rem;
  right: 4rem;
}

.signature-image {
  width: 68px;
  position: absolute;
  left: 8rem;
  bottom: .02rem;
}
.signature-line {
  border-top: 1px solid #333;
  width: 100%;
  max-width: 120px;
  margin: 0 auto 2px auto;
}

.director-name {
  font-size: 7.5px;
  font-weight: bold;
  color: black;
  margin: 0;
  line-height: 1.3;
}

.director-title {
  font-size: 6px;
  color: #666;
  margin: 0;
  line-height: 1.3;
}


@media print {
  /* Masquer tous les éléments de la page principale */
  body > *:not(.card-modal-overlay) {
    display: none !important;
  }
  
  .back-nav, 
  .header-actions, 
  .view-card-btn, 
  .edit-btn, 
  .print-btn, 
  .date-range-controls,
  .agent-view,
  .agent-header,
  .agent-content,
  .agent-details {
    display: none !important;
  }
  
  /* Styles pour le modal lors de l'impression */
  .card-modal-overlay {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    background: white !important;
    padding: 0 !important;
    margin: 0 !important;
    z-index: 9999 !important;
    overflow: visible !important;
  }
  
  .card-modal-content {
    box-shadow: none !important;
    max-width: 100% !important;
    width: 100% !important;
    height: auto !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    background: white !important;
    overflow: visible !important;
  }
  
  .card-modal-header,
  .card-modal-actions {
    display: none !important;
  }
  
  .card-container {
    display: block !important;
    padding: 0 !important;
    margin: 0 !important;
    background: white !important;
  }
  
  .service-card {
    /* Dimensions exactes pour impression: 86mm x 54mm */
    width: 86mm !important;
    height: 54mm !important;
    min-width: 86mm !important;
    min-height: 54mm !important;
    max-width: 86mm !important;
    max-height: 54mm !important;
    page-break-after: always !important;
    break-after: page !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    margin: 0 !important;
    padding: 0 !important;
    box-shadow: none !important;
    border: 1px solid #000 !important;
    position: relative !important;
    display: block !important;
  }
  
  .service-card:last-child {
    page-break-after: auto !important;
    break-after: auto !important;
  }
  
  /* Définir la taille de page pour chaque carte (86mm large x 54mm haut = paysage) */
  @page {
    size: 86mm 54mm;
    margin: 0;
  }
}
</style>
